import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import { customerAppUrl } from './config.ts';

/**
 * "Login as": sign in as somebody, in this browser.
 *
 * The session cookie lives on the API origin and is shared by every tab, so
 * this is not a window into one page — from here on, this browser is that
 * person everywhere. That is why the console carries a banner and the customer
 * app carries one too: the state is browser-wide and has to look like it.
 *
 * The customer app opens in a new tab because that is what the superadmin came
 * for; the console stays where it is, holding the way back out.
 *
 * Stopping lives in `@wundo/client/impersonation.ts`, because the customer app
 * needs it too. Starting is staff-only and stays here.
 */
export function startImpersonation(userId: string): Promise<void> {
  return openCustomerAppAfter(() =>
    apiFetch('/auth/admin/impersonate-user', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  );
}

export interface GuestVisitSettings {
  role: 'user' | 'admin';
  cohort_id: string | null;
}

/** What the next visit to this organisation starts with, so the dialog opens on it. */
export function fetchGuestVisitSettings(
  organisationId: string,
): Promise<GuestVisitSettings> {
  return apiFetchJson<GuestVisitSettings>(
    `/organisations/${organisationId}/guest-account`,
  );
}

/**
 * Work in a customer organisation as yourself, through your own guest account
 * there. The same browser-wide switch as "Login as", ended the same way.
 */
export function startGuestVisit(
  organisationId: string,
  settings: GuestVisitSettings,
): Promise<void> {
  return openCustomerAppAfter(() =>
    apiFetch(`/organisations/${organisationId}/actions/visit`, {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  );
}

async function openCustomerAppAfter(start: () => Promise<unknown>): Promise<void> {
  /*
   * Opened before the request, while this call is still part of the click: a
   * tab opened after an await is a popup to the browser and gets blocked. It
   * starts blank and is pointed at the app only once the session is swapped.
   */
  const appTab = window.open('', '_blank');

  try {
    await start();
  } catch (error) {
    appTab?.close();
    throw error;
  }

  appTab?.location.assign(customerAppUrl());

  /*
   * Same reasoning as stopping, in the other direction: every screen and
   * cached request in this tab was assembled for the superadmin. The reload is
   * also what raises the banner.
   */
  window.location.reload();
}
